import { CURRENT_EXTENSION_ID, STALE_EXTENSION_ID } from "utils/extensionIds";
import { isPluginInstalled } from "utils/plugin";
import { posthog } from "utils/posthog";

/**
 * Which of the two published Equility extensions a person is actually running.
 *
 * Two live Chrome Web Store items ship as "Equility Extension". The stale one
 * (`STALE_EXTENSION_ID`, v1.200.10) is missing the message handlers AutoRec
 * needs, so a trial that installed from that listing cannot reconcile at all —
 * and nothing in any system records which listing a given user installed.
 *
 * 🪤 This is detected from the PAGE, not from inside the extension. A release
 * to the current item is invisible on every install of the stale item: separate
 * Web Store items, separate ids, no upgrade path. Extension-side telemetry can
 * therefore never see the population being measured, and Web Store publishes
 * are failing besides.
 */
export type ExtensionBuild =
	| "current"
	| "stale"
	| "both"
	| "none"
	| "indeterminate";

/**
 * The `<html>` attribute each build's `appService()` writes.
 *
 * 🪤 Named "body mark" after the extension's own `setBodyMark()`, but that
 * helper writes `document.documentElement.setAttribute(mark, '')` — it has
 * always set the attribute on `<html>`, never on `<body>`, at both the stale
 * commit and HEAD. Reading these off `document.body` returns false on every
 * install of both builds. The name is kept because it is the property name
 * PostHog queries are written against.
 */
export type ExtensionBodyMark =
	| "equility-app-service"
	| "equility-extension"
	| "none";

const CURRENT_BUILD_MARK = "equility-app-service";
const STALE_BUILD_MARK = "equility-extension";

export const EXTENSION_BUILD_DETECTED_EVENT = "extension_build_detected";

/** A probe that outlives this is a probe that failed. Keep it off the paint path. */
const PROBE_TIMEOUT_MS = 1500;

/** Web-accessible in both manifests under `icons/*` for `<all_urls>`. */
const PROBE_RESOURCE = "icons/favicon-32x32.png";

export interface ExtensionBuildDetection {
	extension_build: ExtensionBuild;
	extension_current_installed: boolean;
	extension_stale_installed: boolean;
	extension_html_attribute: boolean;
	extension_body_mark: ExtensionBodyMark;
}

/**
 * Ask Chrome for one web-accessible icon from a specific extension id.
 *
 * A load that succeeds proves that exact item is installed and enabled — the
 * only signal here that discriminates the two builds with certainty. Neither
 * manifest sets `use_dynamic_url`, so the URL is static and guessable from the
 * id alone.
 *
 * Resolves `false` on error and on timeout; it never rejects. An uninstalled
 * extension is indistinguishable from a blocked request at this layer, and both
 * mean "not detected" rather than "something went wrong".
 */
function probeExtension(extensionId: string): Promise<boolean> {
	return new Promise((resolve) => {
		let settled = false;
		const settle = (installed: boolean) => {
			if (settled) return;
			settled = true;
			window.clearTimeout(timer);
			resolve(installed);
		};

		const timer = window.setTimeout(() => settle(false), PROBE_TIMEOUT_MS);

		try {
			const image = new Image();
			image.onload = () => settle(true);
			image.onerror = () => settle(false);
			image.src = `chrome-extension://${extensionId}/${PROBE_RESOURCE}`;
		} catch {
			// `new Image()` is not available in every host (jsdom without a DOM,
			// a hardened embedder). Absence of a probe is not an installed build.
			settle(false);
		}
	});
}

function readBodyMark(): ExtensionBodyMark {
	const root = document.documentElement;

	// Order matters. The current build sets BOTH marks: `equility-extension` at
	// content-script top level and `equility-app-service` from `appService()`.
	// Only the stale build sets `equility-extension` alone, so the current
	// build's discriminator has to be tested first.
	if (root.hasAttribute(CURRENT_BUILD_MARK)) return CURRENT_BUILD_MARK;
	if (root.hasAttribute(STALE_BUILD_MARK)) return STALE_BUILD_MARK;
	return "none";
}

function resolveBuild(
	currentInstalled: boolean,
	staleInstalled: boolean,
	htmlAttribute: boolean,
): ExtensionBuild {
	if (currentInstalled && staleInstalled) return "both";
	if (currentInstalled) return "current";
	if (staleInstalled) return "stale";

	// 🪤 `indeterminate` must stay distinct from `none`. Some build marked the
	// page but neither probe answered, which is a failure OF the measurement —
	// folding it into `none` reports "no extension" and turns a broken probe
	// into a confident wrong number.
	return htmlAttribute ? "indeterminate" : "none";
}

/**
 * Resolve the installed build from three signals, cheapest first.
 *
 * The two DOM reads are synchronous and free but weak; the probes are
 * authoritative and run in parallel. Both DOM marks are read BEFORE awaiting,
 * so a mark that lands mid-probe cannot change what is reported.
 */
export async function detectExtensionBuild(): Promise<ExtensionBuildDetection> {
	const htmlAttribute = isPluginInstalled();
	const bodyMark = readBodyMark();

	const [currentInstalled, staleInstalled] = await Promise.all([
		probeExtension(CURRENT_EXTENSION_ID),
		probeExtension(STALE_EXTENSION_ID),
	]);

	return {
		extension_build: resolveBuild(
			currentInstalled,
			staleInstalled,
			htmlAttribute,
		),
		extension_current_installed: currentInstalled,
		extension_stale_installed: staleInstalled,
		extension_html_attribute: htmlAttribute,
		extension_body_mark: bodyMark,
	};
}

/**
 * The user this has already been reported for, at MODULE scope.
 *
 * 🪤 Module scope, not a ref in the calling hook. `useUser` is consumed by 46
 * components, each getting its own hook instance and therefore its own refs —
 * a per-instance guard fires this once per mounted consumer, so one page load
 * sends dozens of events and the "how many trials are on the stale build"
 * count becomes a count of components. The identify call next to it tolerates
 * that (posthog-js drops a repeat identify to the same distinct id); a capture
 * does not.
 */
let reportedUserId: number | null = null;

/**
 * Detect the build and report it once per authenticated session.
 *
 * 🪤 MUST be called after `identifyPostHogUser()`. Fired before identify, the
 * event lands on the anonymous device id instead of the person, and
 * `setPersonProperties` has no person to write to — which defeats the whole
 * point, since the requirement is to filter trials by the build they are on.
 *
 * The person properties are not optional. They are what makes "N trials are on
 * a non-current build" a person filter instead of an event scan over a window.
 *
 * Never throws and never blocks render: a probe that fails, a wedged posthog-js
 * and a missing API key all resolve to a quiet no-op. Analytics is best-effort.
 */
export async function reportExtensionBuild(userId: number): Promise<void> {
	if (reportedUserId === userId) return;
	// Claimed before the first await, so concurrent consumers mounting in the
	// same tick cannot both get past the guard.
	reportedUserId = userId;

	try {
		const detection = await detectExtensionBuild();

		// `environment` is attached by `sanitize_properties` in utils/posthog.ts.
		posthog.capture(EXTENSION_BUILD_DETECTED_EVENT, detection);
		posthog.setPersonProperties({
			extension_build: detection.extension_build,
			extension_stale_installed: detection.extension_stale_installed,
		});
	} catch {
		// Measurement must never break the app it measures.
	}
}

/**
 * Forget the reported user so the next sign-in reports again.
 *
 * Called from logout. Without it, signing out and back in as a different user
 * in the same page load leaves the second person with no build recorded.
 */
export function resetExtensionBuildReport(): void {
	reportedUserId = null;
}
